import { Bot, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useChatbotStore } from '../stores/chatbotStore';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';
import { IMessage } from '../types/types';
import { useSubmitDataCollection } from '../hooks/useChatbotsQuery';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Button } from '@/components';
import { PoweredBy } from '@/constants/images';
import { chatService } from '../services/chat.service';
import { useWebSocket } from '../hooks/useWebSocket';
import { getOrCreateVisitorId, getMediaTypeFromFile } from '../utils/helpers';
import ChatHeader from './ChatHeader';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import WelcomeMessageBox from './WelcomeMessageBox';
import LoadingIndicator from './LoadingIndicator';
import BottomModal from '@/components/BottomModal';
import DataCollectionForm from './DataCollectionForm';

interface BottomModalItem {
  id: string;
  title: string;
  description?: string;
  subtitle?: string;
  customContent?: React.ReactNode;
}

export interface BottomModalSection {
  title?: string;
  items: BottomModalItem[];
}


interface ChatbotPreviewWithChatProps {
  triggerMessage?: string;
  showFrame?: boolean;
  frameHeader?: boolean;
  onMessageSent?: () => void;
  onBotResponseComplete?: () => void;
}

export default function ChatbotPreviewWithChat({ triggerMessage, onMessageSent, onBotResponseComplete, showFrame = false, frameHeader = false }: ChatbotPreviewWithChatProps) {
  const { id: chatbotId } = useParams();
  const { botName, botLogo, welcomeMessage, conversionId: conId, conversionStatus: conStatus, primaryColor, inputPlaceholder, secondaryColor, textColor, questionList, dataFields, dataCompletionCompleted, messagesHistory } = useChatbotStore();

  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const completedStreamingMessageRef = useRef('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [visitorId] = useState(getOrCreateVisitorId());
  const [showDataCollection, setShowDataCollection] = useState(false);
  const [dataCollectionCompleted, setDataCollectionCompleted] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string>('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isStartingConversation, setIsStartingConversation] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync, isPending } = useSubmitDataCollection();

  const handleStartConversation = useCallback(async () => {
    setIsStartingConversation(true);
    try {
      const result = await chatService.startConversation(chatbotId!, visitorId);
      if (result && result.conversationId) {
        setConversationId(result.conversationId);
        useChatbotStore.getState().setConversionId(result.conversationId);
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
    } finally {
      setIsStartingConversation(false);
    }
  }, [chatbotId, visitorId]);

  // Convert dataFields to BottomModal format
  const modalSections = useMemo(() => {
    if (!dataFields || dataFields.length === 0) return [];

    return [{
      title: '',
      items: dataFields.map(field => ({
        id: field._id,
        title: field.label,
        description: field.description,
        subtitle: field.required ? 'Required' : 'Optional',
        type: field.type
      }))
    }];
  }, [dataFields]);


  // Conversation/Agent state
  const [conversationStatus, setConversationStatus] = useState<'bot_active' | 'pending_agent' | 'agent_active' | 'closed'>('bot_active');

  // Media upload state
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Check if we need to show data collection
  const needsDataCollection = dataFields && dataFields.length > 0 && !dataCollectionCompleted;

  // 2. Update scrollToBottom to scroll only the container
  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: behavior
      });
    }
  };


  // Auto-scroll when new content arrives (non-blocking)
  useEffect(() => {
    // Only scroll when there are actual new messages or streaming content
    // Don't scroll while user is typing (inputValue changes)
    if (messages.length > 0 || streamingMessage) {
      requestAnimationFrame(() => {
        scrollToBottom('smooth');
      });
    }
  }, [messages.length, streamingMessage]);

  // Refocus input after bot response completes
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading, messages.length]);

  // Handle triggered messages from test scenarios
  useEffect(() => {
    if (triggerMessage && !isLoading) {
      sendMessageToBot(triggerMessage);
      onMessageSent?.();
    }
  }, [triggerMessage]);

  useEffect(() => {
    setDataCollectionCompleted(dataCompletionCompleted);
  }, [dataCompletionCompleted])

  // Sync messages with messagesHistory from store (API data)
  useEffect(() => {
    setMessages(messagesHistory || []);
  }, [messagesHistory]);

  // Load conversation status from store (parent components handle initialization)
  useEffect(() => {
    if (conStatus) {
      setConversationStatus(conStatus as any);
    }
    if (conId) {
      setConversationId(conId);
    }
  }, [conStatus, conId]);

  // Memoize the loading condition to ensure it's properly calculated
  // Only show the loading indicator when there are already messages (avoid showing on initial page load)
  const shouldShowLoadingIndicator = useMemo(() => {
    // Don't show loading if there's no streaming message and we're just starting up
    if (streamingMessage) return false;

    // Show loading indicator only for actual chat operations (not workflow resume)
    const showForChatLoading = isLoading && messages.length > 0;

    return showForChatLoading;
  }, [isLoading, streamingMessage, messages.length]);


  // WebSocket connection for real-time messages
  useWebSocket({
    visitorId,
    conversationId,
    onMessage: (message) => {
      // Clear loading when bot/agent messages arrive
      if (!message.isFromUser) {
        console.log('🛑 Bot/Agent message received, clearing loading');
        setIsLoading(false);
      }
      // Add message directly - backend prevents duplicates
      setMessages(prev => [...prev, message]);
    },
    onStatusChange: (status) => {
      setConversationStatus(status as any);
    },
    onBotChunk: (chunk) => {
      setStreamingMessage(prev => prev + chunk);
      completedStreamingMessageRef.current += chunk;
    },
    onComplete: () => {
      if (completedStreamingMessageRef.current) {
        const botMessage: IMessage = {
          _id: `msg_${Date.now()}`,
          content: completedStreamingMessageRef.current,
          isFromUser: false,
          createdAt: new Date(),
          type: 'text',
        };
        setMessages(prev => [...prev, botMessage]);
        setStreamingMessage('');
        completedStreamingMessageRef.current = '';
      }
      setIsLoading(false);
      onBotResponseComplete?.();
    },
    onError: (error) => {
      console.error('❌ Error received from WebSocket:', error);
      setIsLoading(false);
      setStreamingMessage('');
      const errorMessage: IMessage = {
        _id: `msg_${Date.now()}`,
        content: error || 'An error occurred. Please try again.',
        isFromUser: false,
        createdAt: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, errorMessage]);
    },
  });


  const sendMessageToBot = useCallback(async (text: string) => {
    if (!text || isLoading || !chatbotId) return;

    // DEBUG: Log current state
    console.log('📤 Sending message:', {
      text,
      conversationId,
      visitorId,
      chatbotId,
      conversationStatus
    });

    if (!conversationId) {
      console.error('❌ Cannot send message: conversationId is null!');
      console.log('💡 This usually means the init API hasn\'t completed yet');
      // Show error to user
      const errorMessage: IMessage = {
        _id: `msg_${Date.now()}`,
        content: 'Please wait for the chat to initialize...',
        isFromUser: false,
        createdAt: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    // Finalize any ongoing streaming message before sending new message
    if (streamingMessage) {
      const botMessage: IMessage = {
        _id: `msg_${Date.now()}`,
        content: streamingMessage,
        isFromUser: false,
        createdAt: new Date(),
        type: 'text',
      };
      setMessages(prev => [...prev, botMessage]);
    }

    const userMessage: IMessage = {
      _id: `msg_${Date.now()}`,
      content: text,
      isFromUser: true,
      createdAt: new Date(),
      type: 'text'
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setStreamingMessage('');

    // Only show loader if bot is handling (not agent)
    if (conversationStatus !== 'agent_active' && conversationStatus !== 'pending_agent') {
      setIsLoading(true);
    }

    // Use unified message API for all messages (workflows and chat)
    try {
      console.log('🚀 Calling chatService.sendMessage with conversationId:', conversationId);
      await chatService.sendMessage(
        conversationId,
        visitorId,
        chatbotId,
        { type: 'text', text }
      );
      console.log('✅ Message sent successfully, waiting for WebSocket response...');
      // Response will come via WebSocket
    } catch (error) {
      console.error('❌ Send message error:', error);
      setIsLoading(false);
      const errorMessage: IMessage = {
        _id: `msg_${Date.now()}`,
        content: 'Sorry, something went wrong. Please try again.',
        isFromUser: false,
        createdAt: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  }, [isLoading, chatbotId, conversationId, visitorId, conversationStatus]);

  // Handle send message (with data collection check)
  const handleSendMessage = useCallback(async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading || !chatbotId) return;

    if (needsDataCollection) {
      setPendingMessage(text);
      setInputValue('');
      setShowDataCollection(true);
      return;
    }

    setInputValue(''); // Clear input first
    await sendMessageToBot(text);
    // Scroll will happen automatically via useEffect watching messages
  }, [inputValue, isLoading, chatbotId, needsDataCollection, sendMessageToBot]);


  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const handleRefresh = () => {
    setMessages([]);
    setConversationId(null);
    setStreamingMessage('');
    setIsLoading(false);
    setConversationStatus('bot_active');
  };

  // Data collection submission handler
  const handleSubmit = useCallback(async (values: Record<string, string>) => {
    try {
      await mutateAsync({
        chatbotId: chatbotId!,
        data: {
          visitorId,
          collectedData: values
        }
      });
      setShowDataCollection(false);
      setDataCollectionCompleted(true);
      // Auto-send the pending message
      if (pendingMessage) {
        sendMessageToBot(pendingMessage);
        setPendingMessage('');
      }
    } catch (error) {
      console.error('Data collection submission error:', error);
    }
  }, [chatbotId, visitorId, mutateAsync, pendingMessage, sendMessageToBot]);


  // Workflow interaction handlers - all use unified message API
  const handleWorkflowButtonClick = useCallback(async (buttonId: string, buttonLabel?: string, contextMessageId?: string) => {
    if (!chatbotId) return;

    // Show what user clicked as a user message
    if (buttonLabel) {
      const userSelectionMessage: IMessage = {
        _id: `msg_${Date.now()}`,
        content: buttonLabel,
        isFromUser: true,
        createdAt: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, userSelectionMessage]);
    }

    // Only show loader if bot is handling
    if (conversationStatus !== 'agent_active' && conversationStatus !== 'pending_agent') {
      setIsLoading(true);
    }

    try {
      await chatService.sendMessage(
        conversationId,
        visitorId,
        chatbotId,
        { type: 'button', buttonId, buttonLabel, contextMessageId: contextMessageId }
      );
      // Response will come via WebSocket
    } catch (error) {
      console.error('Workflow button click error:', error);
      setIsLoading(false);
    }
  }, [chatbotId, visitorId, conversationId, conversationStatus]);

  const handleWorkflowListSelect = useCallback(async (itemId: string, itemTitle?: string, contextMessageId?: string) => {
    if (!chatbotId) return;
    // Show what user selected as a user message
    if (itemTitle) {
      const userSelectionMessage: IMessage = {
        _id: `msg_${Date.now()}`,
        content: itemTitle,
        isFromUser: true,
        createdAt: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, userSelectionMessage]);
    }

    // Only show loader if bot is handling
    if (conversationStatus !== 'agent_active' && conversationStatus !== 'pending_agent') {
      setIsLoading(true);
    }

    try {
      await chatService.sendMessage(
        conversationId,
        visitorId,
        chatbotId,
        { type: 'list_reply', listItemId: itemId, itemTitle, contextMessageId }
      );
      // Response will come via WebSocket
    } catch (error) {
      console.error('Workflow list select error:', error);
      setIsLoading(false);
    }
  }, [chatbotId, visitorId, conversationId, conversationStatus]);

  // Address submission handler
  const handleWorkflowAddressSubmit = useCallback(async (address: string) => {
    if (!chatbotId) return;

    // Show what user submitted as a user message
    const userAddressMessage: IMessage = {
      _id: `msg_${Date.now()}`,
      content: address,
      isFromUser: true,
      createdAt: new Date(),
      type: 'text'
    };
    setMessages(prev => [...prev, userAddressMessage]);

    // Only show loader if bot is handling
    if (conversationStatus !== 'agent_active' && conversationStatus !== 'pending_agent') {
      setIsLoading(true);
    }

    try {
      await chatService.sendMessage(
        conversationId,
        visitorId,
        chatbotId,
        { type: 'text', text: address, messageType: 'ask_address_response' }
      );
      // Response will come via WebSocket
    } catch (error) {
      console.error('Workflow address submit error:', error);
      setIsLoading(false);
    }
  }, [chatbotId, visitorId, conversationId, conversationStatus]);

  // Location capture handler
  const handleWorkflowLocationCapture = useCallback(async (latitude: number, longitude: number) => {
    if (!chatbotId) return;

    // Show what user shared as a user message
    const userLocationMessage: IMessage = {
      _id: `msg_${Date.now()}`,
      content: `📍 Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
      isFromUser: true,
      createdAt: new Date(),
      type: 'location',
      location: { latitude, longitude }
    };
    setMessages(prev => [...prev, userLocationMessage]);

    // Only show loader if bot is handling
    if (conversationStatus !== 'agent_active' && conversationStatus !== 'pending_agent') {
      setIsLoading(true);
    }

    try {
      await chatService.sendMessage(
        conversationId,
        visitorId,
        chatbotId,
        { type: 'location', location: { latitude, longitude } }
      );
      // Response will come via WebSocket
    } catch (error) {
      console.error('Workflow location capture error:', error);
      setIsLoading(false);
    }
  }, [chatbotId, visitorId, conversationId, conversationStatus]);

  // Media upload handler for agent mode
  const handleMediaUpload = useCallback(async (file: File) => {
    if (!chatbotId) return;

    setUploadingMedia(true);
    try {
      console.log('📁 Uploading media for chat:', file.name);

      const result = await chatService.uploadMedia(
        file,
        visitorId,
        chatbotId,
        conversationId || undefined
      );

      const messageType = getMediaTypeFromFile(file);

      // Show the uploaded file as a user message with proper media type
      const mediaMessage: IMessage = {
        _id: `msg_${Date.now()}`,
        content: result.media.originalName,
        isFromUser: true,
        createdAt: new Date(),
        type: messageType,
        mediaUrl: result.media.url
      };
      setMessages(prev => [...prev, mediaMessage]);

      // Send media message to backend
      if (conversationId) {
        await chatService.sendMessage(
          conversationId,
          visitorId,
          chatbotId,
          {
            type: 'media',
            media: {
              url: result.media.url,
              mimeType: result.media.mimeType,
              caption: result.media.originalName
            }
          }
        );
      }

      // Hide the media upload interface
      setShowMediaUpload(false);
      setUploadingMedia(false);

      console.log('✅ Media uploaded successfully');
    } catch (error) {
      console.error('Media upload error:', error);
      setUploadingMedia(false);
      alert('Failed to upload media. Please try again.');
    }
  }, [chatbotId, visitorId, conversationId]);

  const ChatWidget = useMemo(() => (
    <div className={`flex flex-col h-full border-2 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden ${showFrame ? 'rounded-2xl shadow-none' : 'rounded-2xl'}`}
      style={{
        borderColor: primaryColor,
      }}>
      {/* Widget Header */}
      <ChatHeader
        botName={botName}
        botLogo={botLogo}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        fallbackImage={PoweredBy}
      />

      {/* Messages Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Agent Mode Banner */}
        {(conversationStatus === 'agent_active' || conversationStatus === 'pending_agent') && (
          <div className="bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-700 px-4 py-2 text-sm text-blue-800 dark:text-blue-200">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
              {conversationStatus === 'pending_agent'
                ? 'Waiting for an agent to join...'
                : 'Connected with an agent'}
            </div>
          </div>
        )}

        <div ref={messagesContainerRef} style={{
          scrollBehavior: 'smooth',
          overflowAnchor: 'none'
        }} className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50 dark:bg-gray-800">
          <WelcomeMessageBox
            welcomeMessage={welcomeMessage}
            botLogo={botLogo}
            primaryColor={primaryColor}
            fallbackImage={PoweredBy}
            questionList={questionList}
            isLoading={isLoading}
            onQuestionClick={handleSendMessage}
            showStartButton={!conversationId}
            isStartingConversation={isStartingConversation}
            onStartConversation={handleStartConversation}
          />

          {messages.map((message, index) => (
            <ChatMessage
              key={index}
              message={message}
              botLogo={botLogo}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              textColor={textColor}
              isWorkflowActive={true}
              isLoading={isLoading}
              isLastMessage={index === messages.length - 1}
              onWorkflowButtonClick={handleWorkflowButtonClick}
              onWorkflowListSelect={handleWorkflowListSelect}
              onWorkflowAddressSubmit={handleWorkflowAddressSubmit}
              onWorkflowLocationCapture={handleWorkflowLocationCapture}
              onWorkflowMediaUpload={handleMediaUpload}
              fallbackImage={PoweredBy}
              containedModal={showFrame}
            />
          ))}

          {streamingMessage && (
            <div className="flex gap-3 items-start">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                {botLogo ? (
                  <img src={botLogo} onError={(e) => (e.currentTarget.src = PoweredBy)} alt="Bot" className="w-full h-full object-cover rounded-full" />
                ) : (
                  <Bot size={16} className="text-white" />
                )}
              </div>
              <div className="flex-1 max-w-[80%]">
                <div className="bg-white rounded-lg rounded-tl-none px-4 py-3 shadow-sm border border-gray-100 dark:border-gray-700 dark:bg-gray-700/50">
                  <div className="text-sm text-gray-800 prose prose-sm max-w-none dark:text-white">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                        code: ({ children }) => <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-xs">{children}</code>,
                        pre: ({ children }) => <pre className="bg-gray-100 dark:bg-gray-700 p-2 rounded my-2 overflow-x-auto">{children}</pre>,
                        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                        a: ({ href, children }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 underline hover:opacity-80 cursor-pointer"
                          >
                            {children}
                          </a>
                        ),
                        // Add custom text processor to detect and linkify
                      }}
                    >
                      {streamingMessage}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          )}

          {shouldShowLoadingIndicator && (
            <LoadingIndicator
              botLogo={botLogo}
              primaryColor={primaryColor}
              fallbackImage={PoweredBy}
            />
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      {conversationId && (
        <ChatInput
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSend={handleSendMessage}
          onKeyPress={handleKeyPress}
          isLoading={isLoading}
          isResumingWorkflow={false}
          primaryColor={primaryColor}
          placeholder={inputPlaceholder}
          conversationStatus={conversationStatus}
          workflowState={'active'}
          showMediaUpload={showMediaUpload}
          uploadingMedia={uploadingMedia}
          onToggleMediaUpload={() => setShowMediaUpload(!showMediaUpload)}
          onMediaSelect={handleMediaUpload}
          isAskMedia={messages.length > 0 && messages[messages.length - 1].type === 'workflow' && messages[messages.length - 1].workflowData?.messageType === 'media_question'}
        />
      )}
    </div>
  ), [showFrame, primaryColor, secondaryColor, botLogo, botName, welcomeMessage, questionList, inputPlaceholder, messages, streamingMessage, isLoading, inputValue, textColor, handleSendMessage, handleKeyPress, conversationStatus, handleWorkflowButtonClick, handleWorkflowListSelect, showMediaUpload, uploadingMedia, handleMediaUpload, conversationId, isStartingConversation, handleStartConversation]);

  if (isFullScreen) {
    return (
      <div className="fixed inset-0 z-9999 bg-white dark:bg-gray-800 flex flex-col">
        <div className="px-4 py-4 flex items-center justify-between border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center p-1">
              {botLogo ? <img src={botLogo} onError={(e) => (e.currentTarget.src = PoweredBy)} alt="Logo" className="w-full h-full object-contain" /> : <Bot className="text-gray-400" />}
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 leading-tight">{botName}</h1>
              <p className="text-xs text-gray-500 font-medium tracking-wide">Preview</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh Chat"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={() => setIsFullScreen(false)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close Full Screen"
            >
              <Minimize2 size={20} />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-background flex items-center justify-center p-4">
          <div className="w-full max-w-2xl h-full max-h-[850px]">
            {ChatWidget}
          </div>
          {showDataCollection && (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-700 rounded-xl shadow-2xl w-full max-w-md max-h-[80%] overflow-hidden flex flex-col">
                {/* Modal Header - Fixed */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 shrink-0" style={{ backgroundColor: primaryColor }}>
                  <h3 className="text-lg font-semibold text-white">Please provide your information</h3>
                  <p className="text-sm text-white text-opacity-90 mt-1">We need a few details before we can assist you</p>
                </div>

                {/* Modal Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4">
                  <DataCollectionForm
                    dataFields={dataFields || []}
                    onSubmit={handleSubmit}
                    onCancel={() => setShowDataCollection(false)}
                    primaryColor={primaryColor}
                    isSubmitting={isPending}
                    hideFooter={true}
                  />
                </div>

                {/* Modal Footer - Fixed */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 shrink-0">
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setShowDataCollection(false)}
                      disabled={isPending}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={isPending}
                      loading={isPending}
                      className="flex-1"
                      style={{ backgroundColor: primaryColor }}
                      onClick={() => {
                        // Get the form data and submit it
                        const form = document.querySelector('form') as HTMLFormElement;
                        if (form) {
                          const submitEvent = new Event('submit', { bubbles: true, cancelable: true }) as any;
                          form.dispatchEvent(submitEvent);
                        }
                      }}
                    >
                      {isPending ? 'Submitting...' : 'Submit'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (showFrame) {
    return (
      <div className="flex flex-col h-full bg-background">
        {/* Preview Frame Header */}
        {frameHeader && <div className="px-5 py-4 flex items-center justify-between bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-700 flex items-center justify-center p-1">
              {botLogo ? <img src={botLogo} onError={(e) => (e.currentTarget.src = PoweredBy)} alt="Logo" className="w-full h-full object-contain" /> : <Bot className="text-gray-400" />}
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{botName || 'Chatbot'}</h3>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest leading-none mt-0.5">Preview</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
              title="Refresh Chat"
            >
              <RefreshCw size={18} />
            </button>
            <button
              onClick={() => setIsFullScreen(true)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
              title="Expand Preview"
            >
              <Maximize2 size={18} />
            </button>
          </div>
        </div>}

        {/* Chat Content Wrapper */}
        <div className="flex-1 overflow-hidden p-4 flex flex-col items-center justify-center relative">
          <div className="w-full h-full relative">
            {ChatWidget}
            {/* Data Collection Modal - positioned within chat widget */}
            {showDataCollection && (
              <div className="absolute inset-0 z-50 flex items-end justify-center overflow-hidden">
                <div className="w-full bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[60vh] animate-in slide-in-from-bottom duration-300 flex flex-col">
                  {/* Modal Header - Fixed */}
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Please provide your information
                    </h2>
                    <button
                      onClick={() => setShowDataCollection(false)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Modal Content - Scrollable */}
                  <div className="flex-1 overflow-y-auto">
                    <DataCollectionForm
                      dataFields={dataFields || []}
                      onSubmit={handleSubmit}
                      onCancel={() => setShowDataCollection(false)}
                      primaryColor={primaryColor}
                      isSubmitting={isPending}
                      hideFooter={true}
                    />
                  </div>

                  {/* Modal Footer - Fixed */}
                  <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 shrink-0">
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setShowDataCollection(false)}
                        disabled={isPending}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        disabled={isPending}
                        loading={isPending}
                        className="flex-1"
                        style={{ backgroundColor: primaryColor }}
                        onClick={() => {
                          // Get the form data and submit it
                          const form = document.querySelector('form') as HTMLFormElement;
                          if (form) {
                            const submitEvent = new Event('submit', { bubbles: true, cancelable: true }) as any;
                            form.dispatchEvent(submitEvent);
                          }
                        }}
                      >
                        {isPending ? 'Submitting...' : 'Submit'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-96 h-[85vh] z-50">
      {ChatWidget}
      {/* Data Collection Modal with DataCollectionForm */}
      {showDataCollection && (
        <BottomModal
          isOpen={showDataCollection}
          onClose={() => setShowDataCollection(false)}
          onItemSelect={() => {
            // Handle selection if needed
          }}
          title="Please provide your information"
          sections={modalSections}
          showSearch={false}
          customContent={
            <DataCollectionForm
              dataFields={dataFields || []}
              onSubmit={handleSubmit}
              onCancel={() => setShowDataCollection(false)}
              primaryColor={primaryColor}
              isSubmitting={isPending}
              hideFooter={true}
            />
          }
          footer={
            <div className="flex gap-3 p-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowDataCollection(false)}
                disabled={isPending}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={isPending}
                loading={isPending}
                className="flex-1"
                style={{ backgroundColor: primaryColor }}
                onClick={() => {
                  // Get the form Data and submit it
                  const form = document.querySelector('form') as HTMLFormElement;
                  if (form) {
                    const submitEvent = new Event('submit', { bubbles: true, cancelable: true }) as any;
                    form.dispatchEvent(submitEvent);
                  }
                }}
              >
                {isPending ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          }
        />
      )}
    </div>
  );
}
